import api from './api';

/**
 * Golden Signals — the per-user library of finalised signals.
 *
 * A catalogue client, nothing more. There is no execute / arm / scan call here
 * and the backend exposes none; the page this feeds sits beside the Strategies
 * tab, whose cards really do trade, and what keeps the two apart is that this
 * surface has no action to offer.
 *
 * The `/v1` prefix is already in api's baseURL, so paths here are bare.
 * The server wraps every response in { success, data } — unwrapped here so
 * callers deal in rows.
 */

export type GsDirection = 'LONG' | 'SHORT' | 'BOTH' | 'CONTEXT';
export type GsStatus = 'DRAFT' | 'TESTING' | 'CONFIRMED' | 'RETIRED';

export const GS_DIRECTIONS: GsDirection[] = ['LONG', 'SHORT', 'BOTH', 'CONTEXT'];
export const GS_STATUSES: GsStatus[] = ['DRAFT', 'TESTING', 'CONFIRMED', 'RETIRED'];

/** What each status means, shown as help text in the editor so the vocabulary
 *  stays worth having. */
export const GS_STATUS_HELP: Record<GsStatus, string> = {
  DRAFT: 'Written down, not yet tested.',
  TESTING: 'Being measured. Numbers may move.',
  CONFIRMED: 'Measured and kept.',
  RETIRED: 'Tried and it does not work. Kept on purpose — knowing what failed is worth as much as knowing what worked.',
};

export const GS_DIRECTION_HELP: Record<GsDirection, string> = {
  LONG: 'Opens a long.',
  SHORT: 'Opens a short.',
  BOTH: 'Fires in both directions.',
  CONTEXT: 'Not a trade of its own — a condition that gates other signals.',
};

export interface GsCondition { tag?: string; label: string; desc: string }
export interface GsParam { key: string; label: string; value: string; source: string }
export interface GsEvidence {
  dataset: string; metric: string; value: string;
  baseline?: string; win?: string; note?: string;
}
export interface GsIndicator { key: string; label: string; note?: string }
export interface GsRelated { kind: 'strategy' | 'signal' | 'doc'; id: string; label: string }

export interface GoldenSignalPayload {
  v: 1;
  mark?: { label: string; color: string; shape: string; position: string };
  chart?: {
    overlays: string[];
    strategy: string | null;
    candle: string;
    defaultSymbol: string;
    defaultInterval: number;
  };
  indicators?: GsIndicator[];
  timeframes?: { measuredOn: number[]; alsoWorks: number[]; weakOn: number[]; note?: string };
  markets?: string[];
  entry?: GsCondition[];
  exit?: GsCondition[];
  params?: GsParam[];
  evidence?: GsEvidence[];
  notWorking?: GsCondition[];
  related?: GsRelated[];
  sources?: string[];
  notes?: string;
}

export interface GoldenSignalRow {
  id: number;
  slug: string;
  name: string;
  direction: GsDirection;
  status: GsStatus;
  summary: string | null;
  tags: string[];
  payload: GoldenSignalPayload;
  /** Non-null when the row was seeded from a shipped constant — that is what
   *  makes "Restore shipped text" available. It is provenance, not a lock. */
  builtinKey: string | null;
  builtinVersion: number;
  sortOrder: number;
  createdAt: string;
  updatedAt: string;
}

export type GoldenSignalDraft = Partial<
  Pick<GoldenSignalRow, 'name' | 'slug' | 'direction' | 'status' | 'summary' | 'tags' | 'payload' | 'sortOrder'>
>;

/** A brand-new entry: enough structure that the editor has rows to fill in,
 *  and nothing claimed that has not been measured. */
export function emptyPayload(): GoldenSignalPayload {
  return {
    v: 1,
    mark: { label: '', color: '#26a69a', shape: 'arrowUp', position: 'belowBar' },
    chart: { overlays: [], strategy: null, candle: 'Regular', defaultSymbol: '', defaultInterval: 1440 },
    indicators: [],
    timeframes: { measuredOn: [], alsoWorks: [], weakOn: [], note: '' },
    markets: [],
    entry: [],
    exit: [],
    params: [],
    evidence: [],
    notWorking: [],
    related: [],
    sources: [],
    notes: '',
  };
}

/** Fill in any section an older row predates, so the editor never renders an
 *  undefined list. Does not invent content. */
export function hydratePayload(p: GoldenSignalPayload | null | undefined): GoldenSignalPayload {
  const base = emptyPayload();
  const src = (p && typeof p === 'object') ? p : ({} as GoldenSignalPayload);
  return {
    ...base,
    ...src,
    v: 1,
    mark: { ...base.mark!, ...(src.mark || {}) },
    chart: { ...base.chart!, ...(src.chart || {}) },
    timeframes: { ...base.timeframes!, ...(src.timeframes || {}) },
    indicators: src.indicators || [],
    markets: src.markets || [],
    entry: src.entry || [],
    exit: src.exit || [],
    params: src.params || [],
    evidence: src.evidence || [],
    notWorking: src.notWorking || [],
    related: src.related || [],
    sources: src.sources || [],
  };
}

export const goldenSignalsService = {
  list: () => api.get<any>('/golden-signals').then(r => (r.data?.data || []) as GoldenSignalRow[]),
  create: (body: GoldenSignalDraft) =>
    api.post<any>('/golden-signals', body).then(r => r.data?.data as GoldenSignalRow),
  update: (id: number, body: GoldenSignalDraft) =>
    api.put<any>(`/golden-signals/${id}`, body).then(r => r.data?.data as GoldenSignalRow),
  remove: (id: number) => api.delete<any>(`/golden-signals/${id}`).then(() => true),
  restore: (id: number) =>
    api.post<any>(`/golden-signals/${id}/restore`).then(r => r.data?.data as GoldenSignalRow),
  duplicate: (id: number) =>
    api.post<any>(`/golden-signals/${id}/duplicate`).then(r => r.data?.data as GoldenSignalRow),
};

/**
 * The "show me this on a chart" link.
 *
 * `overlays` is an ADDITIVE parameter: ChartsPage turns the named chips ON and
 * touches no others. It is spelled out here rather than in the page so the one
 * place that builds this URL is the one place that has to be right — an entry
 * whose overlay keys are wrong is a button that silently does nothing, which is
 * why verifyGoldenSignals checks every built-in's keys against the Overlays
 * interface.
 */
export function chartLinkFor(row: GoldenSignalRow, symbolOverride?: string): string | null {
  const c = row.payload?.chart;
  const symbol = (symbolOverride || c?.defaultSymbol || '').trim();
  if (!symbol) return null;
  const q = new URLSearchParams();
  if (c?.defaultInterval) q.set('interval', String(c.defaultInterval));
  if (c?.candle) q.set('candle', c.candle);
  if (c?.strategy) q.set('strategy', c.strategy);
  const overlays = (c?.overlays || []).filter(Boolean);
  if (overlays.length) q.set('overlays', overlays.join(','));
  const qs = q.toString();
  return `/charts/${encodeURIComponent(symbol)}${qs ? '?' + qs : ''}`;
}
